import React, { useState, useEffect, useMemo, useRef } from 'react';
import { baseCardsOnly } from "../utils/cardVariants";
import {
  validateDeck,
  calculateDeckStats,
  getCardTypes,
  getCardDomains,
  cardHasChampionTag,
} from "../utils/deckValidation";
import DeckStats from "./DeckStats";
import DomainBadge from "./DomainBadge";
import { saveLocalDeck, updateLocalDeck } from "../utils/localDeckStorage";
import { getCardImageUrl } from "../utils/imageCache";
import { useAuth } from "../contexts/AuthContext";
import { decodeDeckCode, isDeckCode } from "../utils/deckCodeDecoder";

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

/** Counter badge "X/Y" for section headers */
const Counter = ({ current, max }) => (
  <span className="ml-2 text-xs font-mono text-muted-foreground">
    {current}/{max}
  </span>
);

/** Compact card thumbnail used in deck list rows */
const CardThumb = ({ card }) => {
  const isBattlefield = card?.type === 'Battlefield';
  return (
  <div className="flex-shrink-0 rounded border border-border/30 overflow-hidden bg-gray-800" style={{ width: 40, height: 56 }}>
    {card.image_src
      ? <img src={card.image_src} alt={card.name}
          className={`w-full h-full ${isBattlefield ? 'object-contain -rotate-90 scale-125' : 'object-cover'}`} />
      : <div style={{ width: 40, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#888' }}>?</div>
    }
  </div>
  );
};

/** Empty slot placeholder */
const EmptySlot = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center w-full py-6 border-2 border-dashed border-border/40 rounded-xl text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
  >
    <span className="text-3xl mb-1">+</span>
    <span className="text-xs">{label}</span>
  </button>
);

/** Section header row */
const SectionHeader = ({ title, children }) => (
  <div className="flex items-center justify-between mb-2">
    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</h4>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Import helpers (text deck list & deck code)
// ---------------------------------------------------------------------------

function normalizeName(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
    .replace(/[\u2013\u2014\u2015]/g, '-')
    .replace(/\s*,\s*/g, ' ')
    .replace(/\s*-\s*/g, ' ')
    .replace(/\s+/g, ' ');
}

function findCardByName(name, baseCards) {
  const clean = normalizeName(name);
  return baseCards.find((c) => normalizeName(c.name) === clean);
}

function matchCardBySetLabel(decodedId, baseCards) {
  const parts = decodedId.split('-');
  if (parts.length < 2) return null;
  const setCode = parts[0];
  const cardNum = String(parseInt(parts.slice(1).join('-'), 10));
  const setLabel = `${setCode}-${cardNum}`;
  let card = baseCards.find((c) => c.set_label === setLabel);
  if (!card) {
    const padded = `${setCode}-${cardNum.padStart(3, '0')}`;
    card = baseCards.find((c) => c.set_label === padded);
  }
  return card || null;
}

function parseDeckListText(text, baseCards) {
  const lines = text.split('\n').map((l) => l.trim());

  const sections = {
    legend: [],
    champion: [],
    mainDeck: [],
    battlefields: [],
    runes: [],
    sideboard: [],
  };

  const sectionMap = {
    legend: 'legend',
    champion: 'champion',
    maindeck: 'mainDeck',
    'main deck': 'mainDeck',
    main: 'mainDeck',
    battlefields: 'battlefields',
    battlefield: 'battlefields',
    runes: 'runes',
    rune: 'runes',
    sideboard: 'sideboard',
    'side board': 'sideboard',
    side: 'sideboard',
  };

  let currentSection = null;

  for (const line of lines) {
    if (line.length === 0) continue;

    const sectionMatch = line.match(/^(\w[\w\s]*?):$/i);
    if (sectionMatch) {
      const key = sectionMatch[1].toLowerCase().trim();
      currentSection = sectionMap[key] || null;
      continue;
    }

    const inlineMatch = line.match(/^(\w[\w\s]*?):\s*(.+)$/i);
    if (inlineMatch) {
      const key = inlineMatch[1].toLowerCase().trim();
      currentSection = sectionMap[key] || null;
      if (currentSection && inlineMatch[2].trim()) {
        sections[currentSection].push(inlineMatch[2].trim());
      }
      continue;
    }

    if (currentSection && currentSection in sections) {
      const cardMatch = line.match(/^(\d+)\s*x?\s+(.+)$/i);
      if (cardMatch) {
        sections[currentSection].push({
          count: parseInt(cardMatch[1], 10),
          name: cardMatch[2].trim(),
        });
      }
    }
  }

  const totalEntries = Object.values(sections).reduce((sum, s) => sum + s.length, 0);
  if (totalEntries === 0) return null;

  const result = {
    legend: null,
    chosenChampion: null,
    mainCards: [],
    battlefields: [],
    runes: [],
    sideDeck: [],
  };

  const resolveCards = (entriesArr) => {
    const resolved = [];
    for (const entry of entriesArr) {
      const name = typeof entry === 'string' ? entry : entry.name;
      const count = typeof entry === 'string' ? 1 : entry.count;
      const card = findCardByName(name, baseCards);
      if (card) {
        resolved.push({ ...card, count });
      }
    }
    return resolved;
  };

  if (sections.legend.length > 0) {
    const firstEntry = typeof sections.legend[0] === 'string'
      ? { name: sections.legend[0], count: 1 }
      : sections.legend[0];
    const card = findCardByName(firstEntry.name, baseCards);
    if (card) result.legend = { ...card, count: 1 };
  }

  if (sections.champion.length > 0) {
    const resolvedChamps = resolveCards(sections.champion);
    if (resolvedChamps.length > 0) {
      result.chosenChampion = { ...resolvedChamps[0], count: 1 };
    }
  }

  result.mainCards = resolveCards(sections.mainDeck);
  result.battlefields = resolveCards(sections.battlefields).map((c) => ({ ...c, count: 1 }));
  result.runes = resolveCards(sections.runes);
  result.sideDeck = resolveCards(sections.sideboard);

  if (!result.legend) {
    const legendInMain = result.mainCards.find((c) => c.type === 'Legend');
    if (legendInMain) {
      result.legend = { ...legendInMain, count: 1 };
      result.mainCards = result.mainCards.filter((c) => c !== legendInMain);
    }
  }

  if (!result.chosenChampion && result.legend) {
    const legendTags = result.legend.tags || [];
    const champ = result.mainCards.find(
      (c) => c.type === 'Unit' && c.supertype === 'Champion' &&
        c.tags?.some((t) => legendTags.includes(t))
    );
    if (champ) {
      result.chosenChampion = { ...champ, count: 1 };
      // Remove the champion copy that now lives in championZone from mainCards
      // (mirror the legend handling above). Any extra copies stay playable as units.
      const remaining = (Number(champ.count) || 1) - 1;
      result.mainCards = remaining > 0
        ? result.mainCards.map((c) => (c === champ ? { ...c, count: remaining } : c))
        : result.mainCards.filter((c) => c !== champ);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const DeckBuilder = ({ cards: allCards }) => {
  const { user } = useAuth();

  // Una sola stampa "base" per carta (niente varianti Metal/Overnumbered/Alternate Art).
  const baseCards = useMemo(() => baseCardsOnly(allCards || []), [allCards]);

  // ---- library filters ----
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'energy'
  const [energyMin, setEnergyMin] = useState('');
  const [energyMax, setEnergyMax] = useState('');
  const [deckSection, setDeckSection] = useState('All');

  // ---- show legend picker panel ----
  const [showLegendPicker, setShowLegendPicker] = useState(false);

  // ---- deck state (includes sideDeck) ----
  const [deck, setDeck] = useState({
    legend: null,
    chosenChampion: null,
    mainCards: [],
    battlefields: [],
    runes: [],
    sideDeck: [],
  });

  const [savedDecks, setSavedDecks] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDeckId, setEditingDeckId] = useState(null);
  const [filteredCards, setFilteredCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [deckName, setDeckName] = useState('My Deck');
  const [availableDomains, setAvailableDomains] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [validation, setValidation] = useState({ isValid: true, errors: [] });
  const [saving, setSaving] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [rightTab, setRightTab] = useState('deck'); // deck | stats | hand
  const [sampleHand, setSampleHand] = useState([]);
  const [splitRatio, setSplitRatio] = useState(60);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };
  const dividerRef = useRef(null);
  const gridRef = useRef(null);
  const dragCardRef = useRef(null);
  const [gridScrollTop, setGridScrollTop] = useState(0);
  const [gridCols, setGridCols] = useState(2);
  const [gridHeight, setGridHeight] = useState(600);

  // Card height estimate: grid cell width * 1.4 + 50px for text/buttons
  const estimateRowHeight = () => {
    if (!gridRef.current) return 400;
    const gridWidth = gridRef.current.clientWidth;
    const colGap = 12; // gap-3 = 12px
    const cellWidth = (gridWidth - colGap * (gridCols - 1)) / gridCols;
    return cellWidth * 1.4 + 50;
  };

  const visibleCards = useMemo(() => {
    const rowHeight = estimateRowHeight();
    const totalRows = Math.ceil(filteredCards.length / gridCols);
    const bufferRows = 3;
    const startRow = Math.max(0, Math.floor(gridScrollTop / rowHeight) - bufferRows);
    const visibleRows = Math.ceil(gridHeight / rowHeight) + bufferRows * 2;
    const endRow = Math.min(totalRows, startRow + visibleRows);

    const startIdx = startRow * gridCols;
    const endIdx = Math.min(filteredCards.length, endRow * gridCols);

    return { startIdx, endIdx, totalRows, rowHeight, startRow };
  }, [filteredCards, gridCols, gridScrollTop, gridHeight]);

  useEffect(() => {
    const updateCols = () => {
      const w = window.innerWidth;
      if (w >= 1536) setGridCols(5);
      else if (w >= 1280) setGridCols(4);
      else if (w >= 1024) setGridCols(3);
      else if (w >= 768) setGridCols(4);
      else if (w >= 640) setGridCols(3);
      else setGridCols(2);
    };
    updateCols();
    window.addEventListener('resize', updateCols);
    return () => window.removeEventListener('resize', updateCols);
  }, []);

  const scrollPositions = useRef({});

  // Save/restore scroll position per section
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const saved = scrollPositions.current[deckSection];
    if (saved !== undefined) {
      grid.scrollTop = saved;
    }
    return () => {
      scrollPositions.current[deckSection] = grid.scrollTop;
    };
  }, [deckSection]);

  useEffect(() => {
    const divider = dividerRef.current;
    if (!divider) return;
    let startX, startRatio;
    const onDown = (e) => {
      e.preventDefault();
      startX = e.clientX;
      startRatio = splitRatio;
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };
    const onMove = (e) => {
      const container = divider.parentElement;
      if (!container) return;
      const dx = e.clientX - startX;
      const w = container.offsetWidth;
      const r = startRatio + (dx / w) * 100;
      setSplitRatio(Math.min(80, Math.max(20, r)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    divider.addEventListener('mousedown', onDown);
    return () => divider.removeEventListener('mousedown', onDown);
  }, [splitRatio]);

  const drawSampleHand = () => {
    const pool = [];
    deck.mainCards.forEach((card) => {
      for (let i = 0; i < (card.count || 1); i++) {
        pool.push(card);
      }
    });
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setSampleHand(shuffled.slice(0, 7));
  };

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Load saved decks when user changes
  useEffect(() => {
    const loadSavedDecks = async () => {
      if (user) {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (response.ok) {
            const responseJson = await response.json();
            setSavedDecks(responseJson.decks || []);
          }
        } catch (error) {
          console.error('Error loading saved decks:', error);
        }
      } else {
        setSavedDecks([]);
      }
    };
    loadSavedDecks();
  }, [user]);

  // Extract domains from legend once selected
  useEffect(() => {
    if (deck.legend) {
      setAvailableDomains(getCardDomains(deck.legend));
    } else {
      setAvailableDomains([]);
    }
  }, [deck.legend]);

  // Auto-init rune deck to 12 distributed across legend domains (default 6/6).
  // Re-inits if runes don't match the current legend's domains.
  useEffect(() => {
    if (!deck.legend || baseCards.length === 0) return;
    const domains = getCardDomains(deck.legend);
    if (domains.length === 0) return;

    const matches = deck.runes.length > 0 &&
      deck.runes.every(r => domains.includes(getCardDomains(r)[0]));
    if (matches) return;

    const runeCards = domains
      .map(dom => baseCards.find(c => c.type === 'Rune' && getCardDomains(c).includes(dom)))
      .filter(Boolean);
    if (runeCards.length === 0) return;

    const base = Math.floor(12 / runeCards.length);
    const newRunes = runeCards.map((rc, i) => ({
      ...rc,
      count: i === 0 ? 12 - base * (runeCards.length - 1) : base,
    }));
    setDeck(prev => ({ ...prev, runes: newRunes }));
  }, [deck.legend, baseCards]);

  // Validate deck whenever deck content changes
  useEffect(() => {
    const result = validateDeck(deck);
    setValidation(result);
    setWarnings(result.errors);
  }, [deck]);

  // Filter + sort library cards
  useEffect(() => {
    if (!baseCards || baseCards.length === 0) {
      setFilteredCards([]);
      return;
    }

    // Deck section filter — overrides the default Legend/Rune exclusion
    let result;
    switch (deckSection) {
      case 'Legends':
        result = baseCards.filter(card => card.type === 'Legend');
        break;
      case 'MainDeck':
        result = baseCards.filter(card => card.type !== 'Legend' && card.type !== 'Rune' && card.type !== 'Battlefield');
        break;
      case 'Battlefields':
        result = baseCards.filter(card => card.type === 'Battlefield');
        break;
      case 'Runes':
        result = baseCards.filter(card => card.type === 'Rune');
        break;
      default: // 'All' — exclude Legends and Runes
        result = baseCards.filter(card => card.type !== 'Legend' && card.type !== 'Rune');
        break;
    }

    // Domain Identity filter when a legend is selected (rule 103.1.b)
    if (deck.legend) {
      const identity = new Set(getCardDomains(deck.legend));
      result = result.filter((card) => {
        if (card.type === 'Legend') return true;
        const cd = getCardDomains(card);
        if (cd.length === 0) return true;
        return cd.every((d) => identity.has(d));
      });
    }

    result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    setFilteredCards(result);
  }, [baseCards, deck.legend, deckSection]);

  // Load deck from localStorage event or direct storage key
  useEffect(() => {
    const handleLoadDeckEvent = (e) => { loadDeckForEditing(e.detail); };

    const deckToLoad = localStorage.getItem('deckToLoad');
    if (deckToLoad) {
      loadDeckForEditing(JSON.parse(deckToLoad));
      localStorage.removeItem('deckToLoad');
    }

    window.addEventListener('loadDeckForEditing', handleLoadDeckEvent);
    return () => window.removeEventListener('loadDeckForEditing', handleLoadDeckEvent);
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && isEditing && editingDeckId) {
        deleteCurrentDeck();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, editingDeckId, deckName]);

  // ---------------------------------------------------------------------------
  // Deck state handlers (unchanged logic)
  // ---------------------------------------------------------------------------

  const loadDeckForEditing = (savedDeck) => {
    setDeck({
      legend: savedDeck.cards.legend || null,
      chosenChampion: savedDeck.cards.chosenChampion || null,
      mainCards: savedDeck.cards.mainCards || [],
      battlefields: savedDeck.cards.battlefields || [],
      runes: savedDeck.cards.runes || [],
      sideDeck: savedDeck.cards.sideDeck || [],
    });
    setDeckName(savedDeck.name);
    setIsEditing(true);
    setEditingDeckId(savedDeck.id);
  };

  const resetDeck = () => {
    setDeck({
      legend: null,
      chosenChampion: null,
      mainCards: [],
      battlefields: [],
      runes: [],
      sideDeck: [],
    });
    setDeckName('My Deck');
    setIsEditing(false);
    setEditingDeckId(null);
    setAvailableDomains([]);
  };

  const deleteCurrentDeck = async () => {
    if (!isEditing || !editingDeckId) {
      showToast('No deck is currently being edited');
      return;
    }

    if (window.confirm(`Are you sure you want to delete deck "${deckName}"?`)) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks/${editingDeckId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.ok) {
          showToast('Deck deleted successfully!');
          const decksResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (decksResponse.ok) {
            const responseJson = await decksResponse.json();
            setSavedDecks(responseJson.decks || []);
          }
          resetDeck();
        } else {
          showToast('Failed to delete deck');
        }
      } catch (error) {
        console.error('Error deleting deck:', error);
        showToast('Network error occurred while deleting deck');
      }
    }
  };

  const setChosenChampion = (card) => {
    setDeck((prev) => ({ ...prev, chosenChampion: card }));
  };

  /** Adjust a rune count, keeping the total at 12 (only for 2-domain legends). */
  const adjustRune = (targetApiId, delta) => {
    setDeck((prev) => {
      if (prev.runes.length !== 2) return prev;
      const runes = prev.runes.map((r) => ({ ...r }));
      const i = runes.findIndex((r) => r.api_id === targetApiId);
      if (i === -1) return prev;
      const j = i === 0 ? 1 : 0;
      let next = (runes[i].count || 0) + delta;
      if (next < 0) next = 0;
      if (next > 12) next = 12;
      runes[i].count = next;
      runes[j].count = 12 - next;
      return { ...prev, runes };
    });
  };

  const countSignatureCards = (mainCards, legendTags) => {
    return mainCards.reduce((sum, card) => {
      if (card.supertype !== 'Signature') return sum;
      if (cardHasChampionTag(card, legendTags)) return sum + (card.count || 1);
      return sum;
    }, 0);
  };

  const addToDeck = (card) => {
    if (card.type === 'Legend' || card.supertype === 'Legend') {
      if (deck.legend) {
        // Already has a legend — silently replace via setDeck (legend picker handles this)
        setDeck(prev => ({ ...prev, legend: { ...card, count: 1 }, chosenChampion: null, runes: [] }));
        return;
      }
      setDeck(prev => ({ ...prev, legend: { ...card, count: 1 } }));
      return;
    }

    if (!deck.legend) {
      showToast('Please select a Legend first');
      return;
    }

    if (card.type !== 'Battlefield') {
      const cardDomains = getCardDomains(card);
      if (cardDomains.length > 0) {
        const identity = new Set(getCardDomains(deck.legend));
        const valid = cardDomains.every((d) => identity.has(d));
        if (!valid) {
          showToast(`${card.type} "${card.name}" doesn't match your legend's domains: ${[...identity].join(', ')}`);
          return;
        }
      }
    }

    if (card.supertype === 'Champion') {
      const legendTags = deck.legend?.tags || [];
      if (!cardHasChampionTag(card, legendTags)) {
        showToast(`Champion "${card.name}" doesn't match your legend's champion tag: ${legendTags.join(', ')}`);
        return;
      }
    }

    if (card.supertype === 'Signature') {
      const legendTags = deck.legend?.tags || [];
      if (!cardHasChampionTag(card, legendTags)) {
        showToast(`Signature card "${card.name}" doesn't match your legend's champion tag: ${legendTags.join(', ')}`);
        return;
      }
      const existingCount = countSignatureCards(deck.mainCards, legendTags);
      if (existingCount + 1 > 3) {
        showToast('You can only have 3 signature cards with the same champion tag as your legend.');
        return;
      }
    }

    let section = 'mainCards';
    if (card.type === 'Battlefield') section = 'battlefields';
    else if (card.type === 'Rune') section = 'runes';

    const currentSection = deck[section];
    const existingCardIndex = currentSection.findIndex(item =>
      item.id === card.id ||
      item.api_id === card.id ||
      item.id === card.api_id ||
      (item.api_id && item.api_id === card.api_id)
    );

    let maxCopies = 3;
    if (card.type === 'Battlefield') maxCopies = 1;
    else if (card.type === 'Rune') maxCopies = 100;

    if (existingCardIndex !== -1) {
      const existingCard = currentSection[existingCardIndex];
      if (existingCard.count >= maxCopies) {
        showToast(`You can only have ${maxCopies} copy/copies of ${card.name}`);
        return;
      }
      const updatedSection = [...currentSection];
      updatedSection[existingCardIndex] = { ...existingCard, count: existingCard.count + 1 };
      setDeck(prev => ({ ...prev, [section]: updatedSection }));
    } else {
      if (card.type !== 'Rune' && card.type !== 'Battlefield' && card.supertype !== 'Signature') {
        const cardsWithSameName = currentSection.filter(item => item.name === card.name);
        const totalCopiesOfThisName = cardsWithSameName.reduce((sum, item) => sum + (item.count || 1), 0);
        if (totalCopiesOfThisName >= maxCopies) {
          showToast(`You can only have ${maxCopies} copy/copies total of cards named "${card.name}"`);
          return;
        }
      }
      const newCard = { ...card, count: 1, api_id: card.api_id || card.id, id: card.id || card.api_id };
      setDeck(prev => ({ ...prev, [section]: [...prev[section], newCard] }));
    }
  };

  const removeFromDeck = (cardId, section) => {
    setDeck(prev => ({
      ...prev,
      [section]: prev[section].filter(item => item.id !== cardId && item.api_id !== cardId)
    }));
  };

  const updateCardCount = (cardId, newCount, section) => {
    if (newCount <= 0) {
      removeFromDeck(cardId, section);
      return;
    }

    setDeck(prev => {
      const currentSection = [...prev[section]];
      const cardIndex = currentSection.findIndex(item => item.id === cardId || item.api_id === cardId);
      if (cardIndex === -1) return prev;

      const card = currentSection[cardIndex];
      const currentCount = card.count || 1;
      const isIncreasing = newCount > currentCount;

      let maxCopies = 3;
      if (card.type === 'Battlefield') maxCopies = 1;
      else if (card.type === 'Rune') maxCopies = 100;

      if (isIncreasing && newCount > maxCopies) {
        showToast(`You can only have ${maxCopies} copy/copies of ${card.name}`);
        return prev;
      }

      if (isIncreasing && card.type !== 'Rune' && card.type !== 'Battlefield' && card.supertype !== 'Signature') {
        const cardsWithSameName = currentSection.filter(item =>
          item.name === card.name && item.id !== cardId && item.api_id !== cardId
        );
        const totalCopiesOfSameName = cardsWithSameName.reduce((sum, item) => sum + (item.count || 1), 0);
        if (totalCopiesOfSameName + newCount > maxCopies) {
          showToast(`You can only have ${maxCopies} copy/copies total of cards named "${card.name}"`);
          return prev;
        }
      }

      if (isIncreasing && section === 'mainCards') {
        const currentTotal = prev.mainCards.reduce((sum, c) => sum + (c.count || 0), 0);
        const cardCurrentCount = currentSection[cardIndex].count || 0;
        // Rule 103.2: Chosen Champion counts as 1 of the 40-card minimum
        const championSlot = prev.chosenChampion ? 1 : 0;
        const mainDeckCap = 40 - championSlot;
        if (currentTotal - cardCurrentCount + newCount > mainDeckCap) {
          showToast(`Main deck cannot exceed ${mainDeckCap} cards (${championSlot ? '+ 1 Chosen Champion = 40 total' : 'no Chosen Champion yet'}).`);
          return prev;
        }
      }

      if (isIncreasing && section === 'runes') {
        const currentTotal = prev.runes.reduce((sum, r) => sum + r.count, 0);
        const cardCurrentCount = currentSection[cardIndex].count || 0;
        if (currentTotal - cardCurrentCount + newCount > 12) {
          showToast(`Rune deck cannot exceed 12 runes.`);
          return prev;
        }
      }

      const updatedSection = [...currentSection];
      updatedSection[cardIndex] = { ...updatedSection[cardIndex], count: newCount };
      return { ...prev, [section]: updatedSection };
    });
  };

  // Side deck handler — cap 8 total cards
  const addToSideDeck = (card) => {
    if (card.type === 'Legend') return;
    if (!deck.legend) {
      showToast('Please select a Legend first');
      return;
    }
    const currentTotal = (deck.sideDeck || []).reduce((s, c) => s + (c.count || 1), 0);
    if (currentTotal >= 8) {
      showToast('Side deck cannot exceed 8 cards.');
      return;
    }

    const existing = (deck.sideDeck || []).findIndex(item =>
      item.id === card.id || item.api_id === card.id ||
      item.id === card.api_id || (item.api_id && item.api_id === card.api_id)
    );

    if (existing !== -1) {
      const updated = [...deck.sideDeck];
      updated[existing] = { ...updated[existing], count: updated[existing].count + 1 };
      setDeck(prev => ({ ...prev, sideDeck: updated }));
    } else {
      const newCard = { ...card, count: 1, api_id: card.api_id || card.id, id: card.id || card.api_id };
      setDeck(prev => ({ ...prev, sideDeck: [...(prev.sideDeck || []), newCard] }));
    }
  };

  const updateSideDeckCount = (cardId, newCount) => {
    if (newCount <= 0) {
      setDeck(prev => ({
        ...prev,
        sideDeck: prev.sideDeck.filter(item => item.id !== cardId && item.api_id !== cardId)
      }));
      return;
    }
    setDeck(prev => {
      const updated = [...prev.sideDeck];
      const idx = updated.findIndex(item => item.id === cardId || item.api_id === cardId);
      if (idx === -1) return prev;
      const isIncreasing = newCount > updated[idx].count;
      if (isIncreasing) {
        const total = updated.reduce((s, c) => s + (c.count || 1), 0);
        if (total - (updated[idx].count || 1) + newCount > 8) {
          showToast('Side deck cannot exceed 8 cards.');
          return prev;
        }
      }
      updated[idx] = { ...updated[idx], count: newCount };
      return { ...prev, sideDeck: updated };
    });
  };

  // Deck stats
  const deckStats = calculateDeckStats(deck);

  // ── Import / Export handlers ──────────────────────────────────────────────

  const buildDeckExportPayload = () => ({
    name: deckName,
    description: `Deck built with legend: ${deck.legend?.name || 'None'}`,
    cards: {
      legend: deck.legend,
      chosenChampion: deck.chosenChampion,
      mainCards: deck.mainCards,
      battlefields: deck.battlefields,
      runes: deck.runes,
      sideDeck: deck.sideDeck || [],
    },
  });

  const handleExportDeck = () => {
    const payload = buildDeckExportPayload();
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${deckName.replace(/[^a-zA-Z0-9_-]/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Shared import logic ─────────────────────────────────────────────────

  const parseAndImportDeck = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return false;

    // 1) JSON format (native or DotGG)
    if (trimmed[0] === '{' || trimmed[0] === '[') {
      try {
        const data = JSON.parse(trimmed);
        let cards = data.cards;
        if (cards && typeof cards === 'object' && !Array.isArray(cards) && cards.legend) {
          loadDeckForEditing({ id: null, name: data.name || deckName, cards });
          return true;
        }
        if (cards && cards.legend != null && cards.mainCards != null) {
          loadDeckForEditing({ id: null, name: data.name || deckName, cards });
          return true;
        }
        if (data.deck && typeof data.deck === 'object') {
          cards = data.deck;
        }
        const cardEntries = Object.entries(cards || {});
        const runeIds = new Set();
        const bfIds = new Set();
        const mainCardEntries = [];
        let foundLegend = null;
        for (const [id, qty] of cardEntries) {
          const card = baseCards.find((c) => c.api_id === id || c.id === id);
          if (card?.type === 'Rune') {
            runeIds.add(id);
          } else if (card?.type === 'Battlefield') {
            bfIds.add(id);
          } else if (card?.type === 'Legend' || card?.supertype === 'Legend') {
            if (!foundLegend) foundLegend = card;
          } else {
            mainCardEntries.push({ id, qty });
          }
        }
        const imported = {
          legend: foundLegend,
          chosenChampion: null,
          mainCards: mainCardEntries.map(({ id, qty }) => {
            const card = baseCards.find((c) => c.api_id === id || c.id === id);
            return card ? { ...card, count: Number(qty) } : null;
          }).filter(Boolean),
          battlefields: [...bfIds].map((id) => {
            const card = baseCards.find((c) => c.api_id === id || c.id === id);
            return card ? { ...card, count: 1 } : null;
          }).filter(Boolean),
          runes: [...runeIds].map((id) => {
            const card = baseCards.find((c) => c.api_id === id || c.id === id);
            return card ? { ...card, count: Number(cards[id]) } : null;
          }).filter(Boolean),
          sideDeck: [],
        };
        loadDeckForEditing({ id: null, name: data.name || data.humanname || deckName, cards: imported });
        return true;
      } catch {
        // Not JSON, fall through
      }
    }

    // 2) Deck code format (base32)
    if (isDeckCode(trimmed)) {
      const result = decodeDeckCode(trimmed);
      if (result && result.cards.length > 0) {
        const missing = [];
        const imported = {
          legend: null,
          chosenChampion: null,
          mainCards: [],
          battlefields: [],
          runes: [],
          sideDeck: [],
        };

        for (const { id, count } of result.cards) {
          const card = matchCardBySetLabel(id, baseCards);
          if (!card) {
            missing.push(id);
            continue;
          }
          if (card.type === 'Legend') {
            imported.legend = { ...card, count: 1 };
          } else if (card.type === 'Rune') {
            imported.runes.push({ ...card, count });
          } else if (card.type === 'Battlefield') {
            imported.battlefields.push({ ...card, count: 1 });
          } else {
            imported.mainCards.push({ ...card, count });
          }
        }

        if (imported.legend) {
          const legendTags = imported.legend.tags || [];
          const champ = imported.mainCards.find(
            (c) => c.type === 'Unit' && c.supertype === 'Champion' &&
              c.tags?.some((t) => legendTags.includes(t))
          );
          if (champ) {
            imported.chosenChampion = { ...champ, count: 1 };
            const remaining = (Number(champ.count) || 1) - 1;
            imported.mainCards = remaining > 0
              ? imported.mainCards.map((c) => (c === champ ? { ...c, count: remaining } : c))
              : imported.mainCards.filter((c) => c !== champ);
          }
        }

        loadDeckForEditing({ id: null, name: deckName, cards: imported });
        if (missing.length > 0) {
          setWarnings([{ message: `Could not find ${missing.length} card(s) by set code: ${missing.slice(0, 10).join(', ')}${missing.length > 10 ? '...' : ''}` }]);
        }
        return true;
      }
    }

    // 3) Text deck list format
    const deckList = parseDeckListText(trimmed, baseCards);
    if (deckList) {
      loadDeckForEditing({ id: null, name: deckName, cards: deckList });
      return true;
    }

    return false;
  };

  const handleImportClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (parseAndImportDeck(text)) return;
      showToast('Could not parse clipboard content. Supported formats: JSON, deck code, or text deck list.');
    } catch (error) {
      showToast('Failed to read clipboard. Make sure you have granted clipboard permission.');
      console.error('Import clipboard error:', error);
    }
  };

  const handleImportText = () => {
    if (!importText.trim()) return;
    setImporting(true);
    const ok = parseAndImportDeck(importText);
    if (ok) {
      setImportText('');
    } else {
      showToast('Could not parse the text. Supported formats: JSON, deck code, or text deck list.');
    }
    setImporting(false);
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      if (parseAndImportDeck(text)) return;
      showToast('Could not parse file. Supported formats: JSON, deck code, or text deck list.');
    };
    reader.onerror = () => showToast('Failed to read file.');
    reader.readAsText(file);
    e.target.value = '';
  };

  // Save deck handler — includes sideDeck in payload
  const handleSaveDeck = async () => {
    const v = validateDeck(deck);
    if (!v.isValid) {
      setWarnings(v.errors.filter((e) => e.severity === 'error').map((e) => ({ message: `${e.ruleRef}: ${e.message}` })));
      return;
    }

    setSaving(true);

    const deckData = buildDeckExportPayload();

    try {
      let response;
      if (user) {
        if (isEditing && editingDeckId) {
          response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks/${editingDeckId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify(deckData)
          });
        } else {
          response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify(deckData)
          });
        }

        const result = await response.json();
        if (response.ok) {
          showToast(isEditing ? 'Deck updated successfully!' : 'Deck saved successfully!');
          const decksResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (decksResponse.ok) {
            const responseJson = await decksResponse.json();
            setSavedDecks(responseJson.decks || []);
          }
        } else {
          showToast(result.message || (isEditing ? 'Failed to update deck' : 'Failed to save deck'));
        }
      } else {
        let result;
        if (isEditing && editingDeckId && editingDeckId.startsWith('local-')) {
          result = updateLocalDeck({ ...deckData, localId: editingDeckId });
        } else {
          result = saveLocalDeck(deckData);
        }
        if (result) {
          showToast(isEditing ? 'Local deck updated successfully!' : 'Local deck saved successfully!');
          setIsEditing(true);
          setEditingDeckId(result.localId);
        } else {
          showToast(isEditing ? 'Failed to update local deck' : 'Failed to save local deck');
        }
      }
    } catch (error) {
      console.error('Error saving deck:', error);
      showToast('Network error occurred while saving deck');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------
  // Rule 103.2: Chosen Champion counts as 1 of the 40-card minimum
  const mainCardCount = deck.mainCards.reduce((s, c) => s + (c.count || 0), 0) + (deck.chosenChampion ? 1 : 0);
  const runeCount = deck.runes.reduce((s, r) => s + (r.count || 0), 0);
  const sideDeckCount = (deck.sideDeck || []).reduce((s, c) => s + (c.count || 0), 0);

  // Legend candidates (deduped by name)
  const legendCandidates = useMemo(() => {
    const seen = new Set();
    return baseCards
      .filter(c => c.type === 'Legend' || c.supertype === 'Legend')
      .filter(c => { if (seen.has(c.name)) return false; seen.add(c.name); return true; });
  }, [baseCards]);

  // Champions eligible to be Chosen Champion
  const eligibleChampions = useMemo(() => {
    if (!deck.legend) return [];
    return baseCards.filter(card =>
      card.type === 'Unit' &&
      card.supertype === 'Champion' &&
      cardHasChampionTag(card, deck.legend.tags || [])
    );
  }, [baseCards, deck.legend]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderCardRow = (card, section, { showCount = true, onRemove, onIncrease, onDecrease } = {}) => {
    const isInvalid = warnings.some(error =>
      error.card?.id === card.id ||
      (error.id && card.api_id && error.id.includes(card.api_id) && error.section === section)
    );

    return (
      <div
        key={card.api_id || card.id}
        className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
          isInvalid ? 'bg-destructive/20 border-destructive/50' : 'bg-background/20 border-border/30 hover:border-border/50'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <CardThumb card={card} />
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{card.name}</div>
            <div className="text-xs text-muted-foreground">{getCardTypes(card).join(' / ')}{card.supertype ? ` · ${card.supertype}` : ''}</div>
          </div>
        </div>
        {showCount && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {onDecrease && (
              <button className="w-7 h-7 flex items-center justify-center bg-muted hover:bg-muted/80 rounded text-sm transition-colors" onClick={onDecrease}>−</button>
            )}
            <span className="w-6 text-center text-sm font-semibold">{card.count}</span>
            {onIncrease && (
              <button className="w-7 h-7 flex items-center justify-center bg-muted hover:bg-muted/80 rounded text-sm transition-colors" onClick={onIncrease}>+</button>
            )}
            {onRemove && (
              <button className="ml-2 text-xs text-destructive hover:text-destructive/80 transition-colors" onClick={onRemove}>✕</button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render — single screen, two columns
  // ---------------------------------------------------------------------------
  return (
    <div className="w-full flex flex-col lg:flex-row gap-6 flex-1 min-h-0">

      {/* ================================================================= */}
      {/* LEFT COLUMN — Card Library                                         */}
      {/* ================================================================= */}
      <div className="flex flex-col gap-4" style={{ flex: `0 0 ${splitRatio}%`, minWidth: 0, minHeight: 0 }}>

        {/* Quick section filter */}
        <div className="flex gap-1 flex-shrink-0 bg-background pt-2 pb-2">
          {['All', 'Legends', 'MainDeck', 'Battlefields', 'Runes'].map((section) => {
            const label = section === 'MainDeck' ? 'Main Deck' : section;
            const active = deckSection === section;
            return (
              <button
                key={section}
                className={`flex-1 py-2 text-xs font-medium rounded-lg leading-none transition-colors ${
                  active ? 'bg-primary text-primary-foreground' : 'bg-card/30 text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setDeckSection(section)}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Card grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 overflow-y-auto pr-1 pt-2"
          style={{ flex: 1, minHeight: 0 }}
          onScroll={(e) => {
            setGridScrollTop(e.target.scrollTop);
            setGridHeight(e.target.clientHeight);
          }}
        >
          {filteredCards.length > 0 ? (
            <>
              {/* Top spacer */}
              <div style={{ gridColumn: `span ${gridCols}`, height: visibleCards.startRow * visibleCards.rowHeight }} />
              {filteredCards.slice(visibleCards.startIdx, visibleCards.endIdx).map((card) => {
                const isChampion = card.type === 'Unit' && card.supertype === 'Champion' &&
                  deck.legend && cardHasChampionTag(card, deck.legend.tags || []);

              return (
                <div
                  key={card.api_id || card.id}
                  draggable
                  onDragStart={(e) => {
                    dragCardRef.current = card;
                    e.dataTransfer.setData('text/plain', JSON.stringify({ api_id: card.api_id, type: card.type, supertype: card.supertype }));
                    e.dataTransfer.effectAllowed = 'copy';
                    const img = e.target.querySelector('img');
                    if (img) {
                      const ghost = img.cloneNode(true);
                      ghost.style.position = 'absolute';
                      ghost.style.top = '-9999px';
                      ghost.style.width = '100px';
                      ghost.style.height = 'auto';
                      document.body.appendChild(ghost);
                      e.dataTransfer.setDragImage(ghost, 50, 70);
                      setTimeout(() => document.body.removeChild(ghost), 0);
                    }
                  }}
                  onClick={() => setSelectedCard(card)}
                  className="cursor-pointer hover:-translate-y-2.5 hover:drop-shadow-lg hover:z-10 transition-transform"
                >
                  <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '140%' }}>
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
                      No Image
                    </div>
                    {card.image_src && card.type === 'Battlefield' ? (
                      <div className="absolute w-[140%] h-[140%] -top-[20%] -left-[20%]">
                        <img
                          src={card.image_src}
                          alt={card.name || 'Card'}
                          className="w-full h-full object-contain -rotate-90"
                          loading="lazy"
                          decoding="async"
                          fetchpriority="low"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    ) : card.image_src ? (
                      <img
                        src={card.image_src}
                        alt={card.name || 'Card'}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        fetchpriority="low"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : null}
                  </div>
                  <div className="p-2 flex flex-col gap-1">
                    {isChampion && (
                      <button
                        className="w-full py-1 bg-amber-600/80 text-white rounded text-xs hover:bg-amber-600 transition-colors font-medium"
                        onClick={(e) => { e.stopPropagation(); setChosenChampion(card); }}
                      >
                        Set Champion
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {/* Bottom spacer */}
            <div style={{ gridColumn: `span ${gridCols}`, height: (visibleCards.totalRows - visibleCards.startRow - Math.ceil((visibleCards.endIdx - visibleCards.startIdx) / gridCols)) * visibleCards.rowHeight }} />
          </>
          ) : (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              {allCards && allCards.length > 0 ? (
                <>
                  <p>No cards match your filters.</p>
                  {!deck.legend && (
                    <p className="mt-2 text-sm">Select a Legend to filter by domain identity.</p>
                  )}
                </>
              ) : (
                <p>Loading cards...</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div
        ref={dividerRef}
        className="hidden lg:flex w-2 cursor-col-resize bg-border/20 hover:bg-primary/40 transition-colors rounded-full flex-shrink-0 items-center justify-center"
        style={{ marginLeft: -4, marginRight: -4, zIndex: 10 }}
      >
        <div className="w-1 h-8 bg-border/50 rounded-full" />
      </div>

      {/* ================================================================= */}
      {/* RIGHT COLUMN — Deck / Stats / Sample Hand                          */}
      {/* ================================================================= */}
      <div className="flex flex-col gap-4" style={{ flex: `0 0 ${100 - splitRatio}%`, minWidth: 0, minHeight: 0 }}>

        {/* Right column tabs */}
        <div className="flex gap-1">
          {['deck', 'stats', 'hand'].map((tab) => {
            const label = tab === 'deck' ? 'Deck' : tab === 'stats' ? 'Stats' : 'Sample Hand';
            return (
              <button
                key={tab}
                className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  rightTab === tab ? 'bg-primary text-primary-foreground' : 'bg-card/30 text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => { setRightTab(tab); if (tab === 'hand') drawSampleHand(); }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {rightTab === 'deck' && (
          <>
            {/* ---- Legend + Chosen Champion row ---- */}
            <div className="flex gap-4">
              {/* Legend */}
              <div className="flex-1 bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-3 min-w-0"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const card = dragCardRef.current;
                  if (!card) return;
                  if (card.type === 'Legend' || card.supertype === 'Legend') {
                    setDeck(prev => ({ ...prev, legend: { ...card, count: 1 }, chosenChampion: prev.legend ? null : prev.chosenChampion, runes: [] }));
                  } else {
                    addToDeck(card);
                  }
                }}
              >
                <SectionHeader title="Legend">
                  <Counter current={deck.legend ? 1 : 0} max={1} />
                </SectionHeader>
                {deck.legend ? (
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-14 bg-muted/20 overflow-hidden rounded border border-border/30 flex-shrink-0">
                      <img src={getCardImageUrl(deck.legend)} alt={deck.legend.name} className="w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = "/cards/placeholder.jpg"; }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-xs truncate">{deck.legend.name}</div>
                      {availableDomains.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-0.5">
                          {availableDomains.map((d, i) => <DomainBadge key={i} domain={d} />)}
                        </div>
                      )}
                    </div>
                    <button className="text-xs text-destructive hover:text-destructive/80 flex-shrink-0"
                      onClick={() => setDeck(p => ({ ...p, legend: null, chosenChampion: null, runes: [] }))}>✕</button>
                  </div>
                ) : (
                  <EmptySlot label="Choose a Legend" onClick={() => setShowLegendPicker(p => !p)} />
                )}
                {showLegendPicker && (
                  <div className="mt-2 border-t border-border/30 pt-2">
                    <p className="text-xs text-muted-foreground mb-1">Select a Legend:</p>
                    <div className="grid grid-cols-3 gap-1 max-h-40 overflow-y-auto pr-1">
                      {legendCandidates.map((legend) => (
                        <button key={legend.api_id || legend.id}
                          className={`rounded overflow-hidden border transition-all hover:border-primary/60 text-left ${deck.legend?.name === legend.name ? 'border-primary' : 'border-border/30'}`}
                          onClick={() => { addToDeck(legend); setShowLegendPicker(false); }}>
                          <div className="aspect-[2/3] w-full bg-muted/20">
                            <img src={getCardImageUrl(legend)} alt={legend.name} className="w-full h-full object-cover"
                              onError={(e) => { e.target.onerror = null; e.target.src = "/cards/placeholder.jpg"; }} />
                          </div>
                          <div className="p-0.5 text-xs font-medium truncate text-center">{legend.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Chosen Champion */}
              <div className="flex-1 bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-3 min-w-0"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const card = dragCardRef.current;
                  if (card && card.type === 'Unit' && card.supertype === 'Champion') setChosenChampion(card);
                }}
              >
                <SectionHeader title="Chosen Champion">
                  <Counter current={deck.chosenChampion ? 1 : 0} max={1} />
                </SectionHeader>
                {deck.chosenChampion ? (
                  <div className="flex items-center gap-2">
                    <CardThumb card={deck.chosenChampion} />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-xs truncate">{deck.chosenChampion.name}</div>
                      <div className="text-xs text-muted-foreground">Champion</div>
                    </div>
                    <button className="text-xs text-destructive hover:text-destructive/80"
                      onClick={() => setDeck((p) => ({ ...p, chosenChampion: null }))}>✕</button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {deck.legend
                      ? eligibleChampions.length > 0
                        ? 'Set Champion in library'
                        : `No champs for "${(deck.legend.tags || []).join(', ')}"`
                      : 'Select Legend first'}
                  </p>
                )}
              </div>
            </div>

            {/* ---- Runes + Battlefields row ---- */}
            <div className="flex gap-4">
              {/* Runes */}
              <div className="flex-1 bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-3 min-w-0"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const card = dragCardRef.current;
                  if (card && card.type === 'Rune') addToDeck(card);
                }}
              >
                <SectionHeader title="Runes"><Counter current={runeCount} max={12} /></SectionHeader>
                {deck.runes.length > 0 ? (
                  <div className="space-y-1">
                    {deck.runes.map((card) => (
                      <div key={card.api_id || card.id} className="flex items-center justify-between bg-background/20 border border-border/30 rounded p-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <CardThumb card={card} />
                          <div className="min-w-0">
                            <div className="font-medium text-xs truncate">{card.name}</div>
                            <div className="flex gap-0.5 mt-0.5">
                              {getCardDomains(card).map((d, i) => <DomainBadge key={i} domain={d} />)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button className="w-6 h-6 flex items-center justify-center bg-muted hover:bg-muted/80 rounded text-xs transition-colors"
                            onClick={() => adjustRune(card.api_id, -1)}>−</button>
                          <span className="w-4 text-center text-xs font-semibold">{card.count}</span>
                          <button className="w-6 h-6 flex items-center justify-center bg-muted hover:bg-muted/80 rounded text-xs transition-colors"
                            onClick={() => adjustRune(card.api_id, 1)}>+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Select a Legend first.</p>
                )}
              </div>

              {/* Battlefields */}
              <div className="flex-1 bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-3 min-w-0"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const card = dragCardRef.current;
                  if (card && card.type === 'Battlefield') addToDeck(card);
                }}
              >
                <SectionHeader title="Battlefields"><Counter current={deck.battlefields.length} max={3} /></SectionHeader>
                {deck.battlefields.length > 0 ? (
                  <div className="space-y-1">
                    {deck.battlefields.map((card) =>
                      renderCardRow(card, 'battlefields', {
                        showCount: false,
                        onRemove: () => removeFromDeck(card.id || card.api_id, 'battlefields'),
                      })
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Add from library.</p>
                )}
              </div>
            </div>

            {/* ---- Main Deck ---- */}
            <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-4"
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
              onDrop={(e) => {
                e.preventDefault();
                  const card = dragCardRef.current;
                  if (card) addToDeck(card);
              }}
            >
              <SectionHeader title="Main Deck"><Counter current={mainCardCount} max={40} /></SectionHeader>
              {deck.mainCards.length > 0 ? (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {deck.mainCards.map((card) =>
                    renderCardRow(card, 'mainCards', {
                      showCount: true,
                      onDecrease: () => updateCardCount(card.id || card.api_id, (card.count || 1) - 1, 'mainCards'),
                      onIncrease: () => updateCardCount(card.id || card.api_id, (card.count || 1) + 1, 'mainCards'),
                      onRemove: () => removeFromDeck(card.id || card.api_id, 'mainCards'),
                    })
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No cards in main deck yet.</p>
              )}
            </div>

            {/* ---- Side Deck ---- */}
            <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-4"
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
              onDrop={(e) => {
                e.preventDefault();
                  const card = dragCardRef.current;
                  if (card) addToSideDeck(card);
              }}
            >
              <SectionHeader title="Side Deck"><Counter current={sideDeckCount} max={8} /></SectionHeader>
              {(deck.sideDeck || []).length > 0 ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                  {(deck.sideDeck || []).map((card) => {
                    const id = card.id || card.api_id;
                    return (
                      <div key={card.api_id || card.id} className="flex items-center justify-between bg-background/20 border border-border/30 rounded-lg p-2 hover:border-border/50 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          <CardThumb card={card} />
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{card.name}</div>
                            <div className="text-xs text-muted-foreground">{card.type}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button className="w-7 h-7 flex items-center justify-center bg-muted hover:bg-muted/80 rounded text-sm transition-colors"
                            onClick={() => updateSideDeckCount(id, (card.count || 1) - 1)}>−</button>
                          <span className="w-6 text-center text-sm font-semibold">{card.count}</span>
                          <button className="w-7 h-7 flex items-center justify-center bg-muted hover:bg-muted/80 rounded text-sm transition-colors"
                            onClick={() => updateSideDeckCount(id, (card.count || 1) + 1)}>+</button>
                          <button className="ml-2 text-xs text-destructive hover:text-destructive/80 transition-colors"
                            onClick={() => setDeck(prev => ({ ...prev,
                              sideDeck: prev.sideDeck.filter(c => c.id !== id && c.api_id !== id) }))}>✕</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No side deck cards yet. Use "+ Side" on library cards.</p>
              )}
            </div>

            {/* ── Bottom bar: save, import, export ────────────────── */}
            <div className="flex gap-2 pt-2 border-t border-border/30">
              <button
                className="flex-1 bg-primary text-primary-foreground rounded-lg py-2 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                disabled={saving || !deckStats.isValid}
                onClick={handleSaveDeck}
              >
                {saving ? 'Saving…' : (isEditing ? 'Update Deck' : 'Save Deck')}
              </button>
              <button
                className="px-3 py-2 bg-violet-800/30 hover:bg-violet-800/50 text-violet-300 rounded-lg text-sm transition-colors"
                onClick={() => {
                  const text = prompt('Paste deck code or deck list:');
                  if (text && parseAndImportDeck(text)) {
                    // imported successfully
                  }
                }}
                title="Import deck"
              >
                ↓
              </button>
            </div>
          </>
        )}

        {rightTab === 'stats' && (
          <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-4">
            <DeckStats stats={deckStats} deck={deck} />
          </div>
        )}

        {rightTab === 'hand' && (
          <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">Sample Hand (7 cards)</h3>
              <button className="text-xs bg-primary/20 hover:bg-primary/40 text-primary rounded-lg px-3 py-1 transition-colors"
                onClick={drawSampleHand}>Redraw</button>
            </div>
            {sampleHand.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {sampleHand.map((card, i) => (
                  <div key={i} className="rounded-lg overflow-hidden border border-border/30 bg-card/30">
                    <div className="relative" style={{ paddingBottom: '140%' }}>
                      {card.image_src && (
                        <img src={card.image_src} alt={card.name}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy" decoding="async"
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                    </div>
                    <div className="p-1 text-xs font-medium truncate text-center">{card.name}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-8">
                Add cards to the main deck first, then click Redraw.
              </p>
            )}
          </div>
        )}

      </div>{/* end right column */}

      {/* ================================================================= */}
      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-foreground text-background px-4 py-2 rounded-lg text-sm font-medium shadow-lg animate-bounce">
          {toast}
        </div>
      )}

      {/* Card Details Modal                                                 */}
      {/* ================================================================= */}
      {selectedCard && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCard(null)}
        >
          <div
            className="bg-card border border-border/50 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold">{selectedCard.name}</h3>
                <button
                  className="text-2xl text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setSelectedCard(null)}
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="aspect-[2/3] w-full bg-muted/20 flex items-center justify-center rounded-lg overflow-hidden border border-border/50">
                  {getCardImageUrl(selectedCard) ? (
                    <img
                      src={getCardImageUrl(selectedCard)}
                      alt={selectedCard.name}
                      className={`w-full h-full object-cover ${selectedCard.type === 'Battlefield' ? '-rotate-90' : ''}`}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => { e.target.onerror = null; e.target.src = "/cards/placeholder.jpg"; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">No Image</div>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-background/20 p-2 rounded">
                      <h4 className="font-semibold text-muted-foreground text-xs uppercase">Type</h4>
                      <p className="text-sm">{selectedCard.type}</p>
                    </div>
                    <div className="bg-background/20 p-2 rounded">
                      <h4 className="font-semibold text-muted-foreground text-xs uppercase">Supertype</h4>
                      <p className="text-sm">{selectedCard.supertype || '—'}</p>
                    </div>
                    <div className="bg-background/20 p-2 rounded">
                      <h4 className="font-semibold text-muted-foreground text-xs uppercase">Cost</h4>
                      <p className="text-sm">{selectedCard.energy ?? selectedCard.cost ?? 'N/A'}</p>
                    </div>
                    <div className="bg-background/20 p-2 rounded">
                      <h4 className="font-semibold text-muted-foreground text-xs uppercase">Rarity</h4>
                      <p className="text-sm">{selectedCard.rarity}</p>
                    </div>
                    <div className="bg-background/20 p-2 rounded">
                      <h4 className="font-semibold text-muted-foreground text-xs uppercase">Set</h4>
                      <p className="text-sm">{selectedCard.set_label || selectedCard.set_code || '—'}</p>
                    </div>
                    <div className="bg-background/20 p-2 rounded">
                      <h4 className="font-semibold text-muted-foreground text-xs uppercase">Domain</h4>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {getCardDomains(selectedCard).length > 0
                          ? getCardDomains(selectedCard).map((d, i) => <DomainBadge key={i} domain={d} />)
                          : <span className="text-sm">—</span>
                        }
                      </div>
                    </div>
                  </div>

                  {(selectedCard.rules_text || selectedCard.abilities) && (
                    <div className="bg-background/20 p-3 rounded">
                      <h4 className="font-semibold text-muted-foreground text-xs uppercase mb-2">Rules Text</h4>
                      <p className="text-sm">{selectedCard.rules_text || selectedCard.abilities || 'No text'}</p>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 mt-2">
                    {selectedCard.type === 'Unit' && selectedCard.supertype === 'Champion'
                      && deck.legend && cardHasChampionTag(selectedCard, deck.legend?.tags || []) && (
                      <button
                        className="w-full py-2 bg-amber-600/80 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
                        onClick={() => { setChosenChampion(selectedCard); setSelectedCard(null); }}
                      >
                        Set as Chosen Champion
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckBuilder;
